
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Clock, Save, Eye } from 'lucide-react';
import { useBlogStore } from '../../store/useBlogStore';
import { usePostQuery } from '../../hooks/usePosts';
import { BlockEditor } from './BlockEditor';
import { PostSettingsPanel } from '../PostSettings/PostSettingsPanel';
import { RevisionHistory } from '../RevisionHistory/RevisionHistory';
import { PostPreview } from '../PostPreview/PostPreview';

export const PostEditor = ({ postId }: { postId?: string }) => {
  const navigate = useNavigate();
  const { activePanel, setActivePanel, unsavedChanges, previewMode, setPreviewMode } = useBlogStore();
  const [title, setTitle] = useState('');
  const { data: post } = usePostQuery(postId ?? '');

  useEffect(() => {
    if (post?.title) setTitle(post.title);
  }, [post?.title]);

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <header className="h-14 border-b flex items-center justify-between px-4 shrink-0 bg-card">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/blog')} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-muted-foreground">{unsavedChanges ? 'Unsaved changes' : 'Saved'}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={() => setPreviewMode(true)} className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center space-x-1">
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">Preview</span>
          </button>
          <button className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary/80 flex items-center space-x-2">
            <Save className="w-4 h-4" />
            <span>Save Draft</span>
          </button>
          <button className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90">
            Publish
          </button>
          <div className="w-px h-6 bg-border mx-2" />
          <button onClick={() => setActivePanel(activePanel === 'settings' ? null : 'settings')} className={`p-2 rounded-md ${activePanel === 'settings' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            <Settings className="w-5 h-5" />
          </button>
          <button onClick={() => setActivePanel(activePanel === 'revisions' ? null : 'revisions')} className={`p-2 rounded-md ${activePanel === 'revisions' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            <Clock className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-y-auto p-8 lg:p-12">
          <div className="max-w-3xl mx-auto space-y-8">
            <input
              type="text"
              placeholder="Untitled post"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full text-4xl lg:text-5xl font-bold bg-transparent border-none outline-none placeholder:text-muted-foreground text-foreground"
            />
            <BlockEditor />
          </div>
        </main>
        
        {activePanel === 'settings' && <PostSettingsPanel />}
        {activePanel === 'revisions' && <RevisionHistory />}
      </div>

      {previewMode && <PostPreview onClose={() => setPreviewMode(false)} />}
    </div>
  );
};
